import Vue from 'vue'
import { RemoteData } from '@/modules/shared/helpers/http/remote-data'
import { Paginatable } from '@/modules/shared/helpers/http/paginatable'
import { Paginated } from '@/modules/shared/models/data'
import { VueClass } from '@niknevis/vue-modules'

// Default Filter Components
import ComboBox from '@/modules/common/filter/components/shared/form/ComboBox.vue'
import SelectFilter from '@/modules/common/filter/components/shared/form/SelectFilter.vue'
import DateRange from '@/modules/common/filter/components/shared/form/DateRange.vue'
import NumericRange from '@/modules/common/filter/components/shared/form/NumericRange.vue'
import Radio from '@/modules/common/filter/components/shared/form/Radio.vue'
import Text from '@/modules/common/filter/components/shared/form/TextBox.vue'
import ToggleFilter from '@/modules/common/filter/components/shared/form/ToggleFilter.vue'

export enum FilterTypes {
  DateRange = 'DateRange',
  NumericRange = 'NumericRange',
  Radio = 'Radio',
  ComboBox = 'ComboBox',
  Select = 'Select',
  Text = 'Text',
  Toggle = 'Toggle',
  Custom = 'Custom'
}

type BoxFilterOptions<T> = {
  type: FilterTypes.ComboBox | FilterTypes.Radio | FilterTypes.Select
  items: T extends Paginated<infer U> ? Paginatable<Paginated<U>> : any[] | RemoteData<T>,
  key: string
}

export enum NumberPresetTypes {
  equals = 'equals',
  lessThan = 'lessThan',
  greaterThan = 'greaterThan',
  range = 'range'
}

export type CustomNumberPreset = {
  label: string
  to: number | null
  from: number | null
}

export const numberPresetNameMap: Record<NumberPresetTypes, string> = {
  [NumberPresetTypes.equals]: 'برابر با',
  [NumberPresetTypes.lessThan]: 'کوچکتر از',
  [NumberPresetTypes.greaterThan]: 'بزرگتر از',
  [NumberPresetTypes.range]: 'بازه دلخواه'
}

export enum DatePresetTypes {
  today = 'today',
  tomorrow = 'tomorrow',
  yesterday = 'yesterday',
  currentWeek = 'currentWeek',
  currentMonth = 'currentMonth',
  lastMonth = 'lastMonth',
  last7Days = 'last7Days',
  custom = 'custom'
}

export const datePresetNameMap: Record<DatePresetTypes, string> = {
  [DatePresetTypes.today]: 'امروز',
  [DatePresetTypes.tomorrow]: 'فردا',
  [DatePresetTypes.yesterday]: 'دیروز',
  [DatePresetTypes.currentWeek]: 'هفته جاری',
  [DatePresetTypes.currentMonth]: 'ماه جاری',
  [DatePresetTypes.lastMonth]: 'ماه گذشته',
  [DatePresetTypes.last7Days]: '7 روز گذشته',
  [DatePresetTypes.custom]: 'بازه دلخواه'
}

type DateRangeFilterOptions = {
  type: FilterTypes.DateRange
  presets?: DatePresetTypes[],
  keys: {
    from: string,
    to: string
  }
}

type NumericRangeFilterOptions = {
  type: FilterTypes.NumericRange
  presets?: (NumberPresetTypes | CustomNumberPreset)[]
  keys: {
    from: string,
    to: string
  }
  min: number
  max: number
}

type CustomFilterOptions<T> = {
  type: FilterTypes.Custom
  component: Vue | VueClass<any>
  items?: T extends Paginated<infer U> ? Paginatable<Paginated<U>> : any[] | RemoteData<T>,
  key?: string
  keys?: {
    from: string,
    to: string
  }
  props?: any
}

type TextFilterOptions = {
  type: FilterTypes.Text
  key: string
}

type ToggleFilterOptions = {
  type: FilterTypes.Toggle
  key: string
}

type AllFilterOptions<T> =
  BoxFilterOptions<T> |
  DateRangeFilterOptions |
  NumericRangeFilterOptions |
  TextFilterOptions |
  ToggleFilterOptions |
  CustomFilterOptions<T>

export type FilterOptions<T> = {
  title: string
  holder?: string
  typeMode?: Boolean
  default?: any
} & AllFilterOptions<T>

export class Filter<T = any> {
  private readonly _type: FilterTypes
  private readonly _title: string
  private readonly _holder: string
  private readonly _typeMode: Boolean
  private readonly _items?: T extends Paginated<infer U> ? Paginatable<Paginated<U>> : any[] | RemoteData<T>
  private readonly _presets?: any[]
  private readonly _key?: string
  private readonly _keys?: {
    from: string,
    to: string
  }
  private readonly _min?: number
  private readonly _max?: number
  private readonly _defaultValue?: any
  private readonly _component?: Vue | VueClass<any>
  private readonly _props: any

  constructor(
    options: FilterOptions<T>
  ) {
    this._type = options.type
    this._title = options.title
    this._holder = options.holder
    this._typeMode = options.typeMode
    this._defaultValue = options.default

    switch (options.type) {
      case FilterTypes.ComboBox:
      case FilterTypes.Select:
      case FilterTypes.Radio:
        this._key = options.key
        this._items = options.items
        break
      case FilterTypes.DateRange:
        this._presets = options.presets
        this._keys = options.keys
        break
      case FilterTypes.NumericRange:
        this._keys = options.keys
        this._presets = options.presets
        this._min = options.min
        this._max = options.max
        break
      case FilterTypes.Custom:
        this._key = options.key
        this._items = options.items
        this._component = options.component
        this._keys = options.keys
        this._props = options.props
        break
      case FilterTypes.Text:
        this._key = options.key
        break
      case FilterTypes.Toggle:
        this._key = options.key
        break
      default:
        throw new Error(`Unknown filter type: ${options}`)
    }
  }

  getComponent() {
    switch (this.type) {
      case FilterTypes.ComboBox:
        return ComboBox
      case FilterTypes.Select:
        return SelectFilter
      case FilterTypes.DateRange:
        return DateRange
      case FilterTypes.NumericRange:
        return NumericRange
      case FilterTypes.Radio:
        return Radio
      case FilterTypes.Text:
        return Text
      case FilterTypes.Toggle:
        return ToggleFilter
      case FilterTypes.Custom:
        return this._component
      default:
        return null
    }
  }

  get type() {
    return this._type
  }

  get items() {
    return this._items
  }

  get presets() {
    return this._presets
  }

  get key() {
    return this._key
  }

  get keys() {
    return this._keys
  }

  get title() {
    return this._title
  }

  get holder() {
    return this._holder
  }

  get typeMode() {
    return this._typeMode
  }

  get min() {
    return this._min
  }

  get max() {
    return this._max
  }

  get defaultValue() {
    return this._defaultValue
  }

  get props() {
    return this._props
  }
}
